import { type DateSchema, type NumberSchema, type Schema, type StringSchema } from 'yup';
import { type TFunction } from 'react-i18next';

import { getErrorMessage } from '@wf/sw-ui-kit';

import {
  EMAIL_MESSAGE,
  MAX_DATE_MESSAGE,
  MAX_MESSAGE,
  MIN_MESSAGE,
  MIN_NUMBER_MESSAGE,
  POSITIVE_MESSAGE,
} from '../constants/validationMessages';
import { ConstraintType, type TValidationConfig } from '../types/configs';
import { FieldPrefix } from '../constants/fieldPrefixes';

import { fieldNameConverter } from '../helpers/fieldNameConverter';
import i18n from '../../../configs/i18n';

function getValidationsFromConfig(validationConfig: TValidationConfig, field: string, schema: Schema) {
  const fieldName = fieldNameConverter(field);

  if (Object.hasOwn(validationConfig, field) || fieldName.includes(FieldPrefix.additionalField)) {
    const constraints = validationConfig[fieldName] || validationConfig['additionalFields.fieldValue'];

    Object.entries(constraints || {}).forEach(([type, { min, max, message, regexp }]) => {
      switch (type) {
        case ConstraintType.size:
          if (max) {
            schema = (schema as StringSchema).max(Number(max), () => i18n.t(MAX_MESSAGE, { max }) as string);
          }

          if (min) {
            schema = (schema as StringSchema).min(Number(min), () => i18n.t(MIN_MESSAGE, { min }) as string);
          }

          return;

        case ConstraintType.pattern:
          schema = (schema as StringSchema).matches(RegExp(regexp), getErrorMessage(i18n.t as TFunction, message));

          return;

        case ConstraintType.positive:
          schema = (schema as NumberSchema).positive(() => i18n.t(POSITIVE_MESSAGE) as string);

          return;

        case ConstraintType.positiveOrZero:
          schema = (schema as NumberSchema).min(0, () => i18n.t(MIN_NUMBER_MESSAGE, { min: 0 }) as string);

          return;

        case ConstraintType.email:
          schema = (schema as StringSchema).email(() => i18n.t(EMAIL_MESSAGE) as string);

          return;

        case ConstraintType.pastOrPresent:
          schema = (schema as DateSchema).max(
            new Date(),
            () => i18n.t(MAX_DATE_MESSAGE, { max: new Date() }) as string
          );

          return;

        // types "notNull" and "notBlank" not present here, because they're covered from mandatory config validations

        default:
          return;
      }
    });
  }

  return schema;
}

export default getValidationsFromConfig;
